import { HubConnection, HubConnectionBuilder, ILogger, LogLevel, RetryContext } from '@microsoft/signalr';

const successStyles = 'color:green;font-size:1.1em;font-weight:bold';
const errorStyles = 'color:red;font-size:1.1em;font-weight:bold';

const MAX_TOTAL_RETRY_TIME = 5 * 60 * 1000;
const MAX_RETRY_DELAY = 8000;
const MAX_RETRY_ATTEMPTS = ((): number => {
  let retryTime = 0;
  let attempts = 0;
  let elapsedMilliseconds = 0;

  while (retryTime <= MAX_TOTAL_RETRY_TIME) {
    elapsedMilliseconds = getNextRetryDelay(attempts);
    retryTime += elapsedMilliseconds;
    attempts++;
  }

  console.log(`Max hub reconnection attempts: ${attempts}`);
  return attempts;
})();

class CustomLogger implements ILogger {
  private readonly logLevel: LogLevel;
  private readonly color: string;

  constructor(logLevel: LogLevel) {
    this.logLevel = logLevel;
    this.color = this.generateColor();
  }

  log(logLevel: LogLevel, message: string): void {
    if (logLevel >= this.logLevel) {
      console.log(`%c⚡${new Date().toISOString()} [${LogLevel[logLevel]}] ${message}`, `color:${this.color}`);
    }
  }

  generateColor(): string {
    const minBrightness = 64; // Минимальная яркость для хорошей видимости на белом фоне

    // Генерируем случайные значения для красного, зеленого и синего компонентов цвета
    const red = Math.floor(Math.random() * 256);
    const green = Math.floor(Math.random() * 256);
    const blue = Math.floor(Math.random() * 256);

    // Вычисляем яркость цвета
    const brightness = (red * 299 + green * 587 + blue * 114) / 1000;

    // Проверяем яркость цвета и корректируем его, если необходимо
    if (brightness < minBrightness) {
      // Вычисляем разницу между текущей яркостью и минимальной яркостью
      const brightnessDiff = minBrightness - brightness;

      // Распределяем разницу между компонентами цвета
      const brightnessFactor = brightnessDiff / brightness;
      const newRed = Math.min(255, Math.floor(red * (1 + brightnessFactor)));
      const newGreen = Math.min(255, Math.floor(green * (1 + brightnessFactor)));
      const newBlue = Math.min(255, Math.floor(blue * (1 + brightnessFactor)));

      return `rgb(${newRed}, ${newGreen}, ${newBlue})`;
    }

    // Если яркость цвета уже достаточно высока, возвращаем исходный цвет
    return `rgb(${red}, ${green}, ${blue})`;
  }
}

export function startConnection(
  url: string,
  {
    logLevel,
    connectHandler: callback,
  }: {
    logLevel?: signalR.LogLevel;
    connectHandler?: VoidFunction;
  } = undefined
): {
  connection: HubConnection;
  callback: Promise<void>;
} {
  const hubConnection = new HubConnectionBuilder()
    .withUrl(url)
    .configureLogging(new CustomLogger(logLevel ?? LogLevel.Information))
    .withAutomaticReconnect({ nextRetryDelayInMilliseconds })
    .build();

  hubConnection.onreconnected(callback);

  return {
    connection: hubConnection,
    callback: hubConnection.start().then(() => {
      callback?.();
    }),
  };
}

export function connectToGroup<R = any>(
  connection: HubConnection,
  id: number,
  methodName: string = 'ConnectToGroup',
  params: (string | number)[] = []
) {
  return connection
    .invoke<R>(methodName, id, ...params)
    .then(res => {
      console.log(`%c✅ Hub ${connection.baseUrl}: Connected to group`, successStyles);
      return res;
    })
    .catch(err => {
      console.log(`%c⛔ Hub ${connection.baseUrl}: Connect to group fail`, errorStyles);
      console.error(err);
      throw err;
    });
}

function nextRetryDelayInMilliseconds({ elapsedMilliseconds, previousRetryCount }: RetryContext): number | null {
  console.log('Hub retry elapsed milliseconds:', elapsedMilliseconds);

  if (previousRetryCount + 1 >= MAX_RETRY_ATTEMPTS) {
    console.log(
      `%c⛔ The number of reconnection attempts (${previousRetryCount}) has been exhausted. Failed to establish a connection`,
      errorStyles
    );
    return null;
  }

  return getNextRetryDelay(previousRetryCount);
}

function getNextRetryDelay(previousRetryCount: number) {
  const nextDelay = getNextDelay(previousRetryCount);

  if (nextDelay > MAX_RETRY_DELAY) {
    return MAX_RETRY_DELAY;
  }

  return nextDelay;
}

function getNextDelay(currentNumber: number): number {
  return (currentNumber + Math.floor(currentNumber / 2)) * 1000;
}
